import { useCallback, useRef, useState } from 'react';
import { Popover } from '@/components/common/Popover';
import { Spinner } from '@/components/common/states';
import type { ExportFormat } from '@/lib/export';
import type { GridMode } from '@/cache/types';

export interface ExportMenuProps {
  readonly disabled: boolean;
  /** Drives the hint about where the export will run. */
  readonly mode: GridMode;
  readonly rowCount: number;
  /**
   * Runs one export. Reports progress so a long server-side export does not
   * look like a hang.
   */
  readonly onExport: (format: ExportFormat, report: (message: string) => void) => Promise<void>;
}

const FORMATS: readonly { format: ExportFormat; label: string }[] = [
  { format: 'parquet', label: 'Parquet' },
  { format: 'csv', label: 'CSV' },
  { format: 'json', label: 'JSON' },
];

export function ExportMenu({ disabled, mode, rowCount, onExport }: ExportMenuProps): JSX.Element {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const local = mode === 'local';

  const run = useCallback(
    async (format: ExportFormat): Promise<void> => {
      setError(null);
      setProgress('Preparing…');
      try {
        await onExport(format, setProgress);
        setProgress(null);
        setOpen(false);
      } catch (cause) {
        setProgress(null);
        setError(cause instanceof Error ? cause.message : 'Export failed.');
      }
    },
    [onExport],
  );

  const busy = progress !== null;

  return (
    <div>
      <button
        ref={anchorRef}
        type="button"
        className="btn"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        Export
        {busy ? <span className="text-[10px] text-local">…</span> : null}
      </button>

      {open ? (
        <Popover
          anchorRef={anchorRef}
          onClose={() => {
            // Never close mid-export: the progress line is the only feedback.
            if (!busy) setOpen(false);
          }}
          width={264}
          align="end"
          role="menu"
          ariaLabel="Export result"
          testId="export-menu"
        >
          <p className="border-b border-line px-2.5 py-1.5 text-[10px] leading-4 text-faint">
            {local
              ? 'Exported from the in-browser cache. No round trip.'
              : 'This result is not cached, so the export runs on the server and can cover far more rows than the grid holds.'}
          </p>

          {FORMATS.map((entry) => (
            <button
              key={entry.format}
              type="button"
              role="menuitem"
              disabled={busy}
              className="flex w-full items-baseline gap-2 px-2.5 py-1.5 text-left text-xs text-ink transition-colors hover:bg-raised disabled:opacity-50"
              onClick={() => void run(entry.format)}
            >
              {entry.label}
              <span className="ml-auto text-[10px] text-faint">
                {local ? `${rowCount.toLocaleString()} rows` : 'server'}
              </span>
            </button>
          ))}

          {busy ? (
            <div className="border-t border-line px-2.5 py-2" role="status" aria-live="polite">
              <Spinner label={progress ?? 'Working…'} />
              <p className="mt-1 text-[10px] text-faint">
                You can keep working; this continues in the background.
              </p>
            </div>
          ) : null}

          {error ? (
            <p
              className="border-t border-line bg-danger-dim px-2.5 py-1.5 text-[10px] leading-4 text-danger"
              role="alert"
            >
              {error}
            </p>
          ) : null}
        </Popover>
      ) : null}
    </div>
  );
}
